// In-app agreement signing (docs/contract.md §10). One firm signer, then the Vertekal countersigner.
// The state machine, the e-signature consent text and the signer display rules, shared by api and web.
// Ported from the Upstream Portal's e-signature module (frontend/src/lib/esignatureSignerProgress.ts,
// backend/src/lib/esignatureCompletion.ts) and trimmed to the two-signer sequential case.
export const AGREEMENT_STATUSES = ['draft', 'sent', 'firm_signed', 'executed', 'declined', 'voided'];
export const AGREEMENT_STATUS_LABEL = {
    draft: 'Draft', sent: 'Sent', firm_signed: 'Firm signed', executed: 'Fully signed', declined: 'Declined', voided: 'Voided',
};
/** Where each action takes an agreement. An action missing from a status is refused (409). */
const MOVES = {
    draft: { send: 'sent', void: 'voided' },
    sent: { firm_sign: 'firm_signed', firm_decline: 'declined', remind: 'sent', void: 'voided' },
    firm_signed: { countersign: 'executed', void: 'voided' },
    executed: {},
    declined: {},
    voided: {},
};
/** The status after `action`, or null when the action isn't allowed from `status`. */
export const agreementNext = (status, action) => MOVES[status][action] ?? null;
/** Declined and voided agreements are history; an onboarding has at most one agreement that isn't. */
export const isActiveAgreement = (status) => status !== 'declined' && status !== 'voided';
/** Upload limit for the agreement PDF. */
export const AGREEMENT_MAX_BYTES = 20 * 1024 * 1024;
/**
 * Consent shown above the Sign button and stored per signer by `version`. The first two sentences of the
 * disclosure and the checkbox's signature clause are the portal's (db/migrations/030 disclosure default,
 * SignDocument.tsx checkbox); the electronic-records consent and the paper option are added here.
 * Never edit a published version: add a new one and bump `version`.
 */
export const ESIGN_CONSENT = {
    version: 'esign-consent-v1',
    disclosure: 'By typing your name below and clicking Sign, you acknowledge you have read this document and agree to its contents. '
        + 'Your electronic signature is the legal equivalent of your handwritten signature under ESIGN and UETA. '
        + 'You can download this agreement before you sign and the fully signed copy afterwards. '
        + 'If you would rather sign on paper, decline and say so, and we will send a paper copy.',
    checkbox: 'I agree to use electronic records and signatures for this agreement, and that typing my name above constitutes '
        + 'my electronic signature and has the same legal effect as a handwritten signature.',
};
/**
 * The firm has put its signature on an agreement (signed in the app, awaiting Vertekal's countersignature, or the
 * Sign the agreement step ticked by hand), so its onboarding is kept for the record and can't be deleted.
 * The review's Delete dialog and the delete endpoint both use it.
 */
export const firmHasSigned = (agreements, steps) => agreements.some((a) => a.status === 'firm_signed' || a.status === 'executed') || steps.some((s) => s.id === 'plan.agreement' && s.state === 'done');
/** Signer display state (portal signerDisplayState + sequential turn order). */
export function signerState(s, all) {
    if (s.status !== 'pending')
        return s.status;
    const earlier = all.some((o) => o.order < s.order && o.status !== 'signed');
    if (earlier)
        return 'waiting';
    return s.viewedAt ? 'viewed' : 'pending';
}
export const SIGNER_STATE_LABEL = {
    waiting: 'Waiting', pending: 'Sent', viewed: 'Viewed', signed: 'Signed', declined: 'Declined', voided: 'Voided',
};
/** "1 of 2 signed · Waiting on Ryan Robertson" (portal signatureProgressLabel, sequential). */
export function agreementProgressLabel(a) {
    const total = a.signers.length;
    const signed = a.signers.filter((s) => s.state === 'signed').length;
    switch (a.status) {
        case 'draft': return 'Not sent';
        case 'executed': return `${signed} of ${total} signed`;
        case 'declined': return 'Declined by the firm';
        case 'voided': return 'Voided';
        default: {
            const current = a.signers.find((s) => s.state === 'pending' || s.state === 'viewed');
            return `${signed} of ${total} signed · Waiting on ${current?.name ?? 'the next signer'}`;
        }
    }
}
//# sourceMappingURL=agreement.js.map